/**
 * The data table, with its header, its empty row and its sort in one place.
 *
 * Twenty-six files had written their own `thead`, and they disagreed on
 * density, on where the empty row went, and on whether narrow screens dropped
 * anything at all. One of them built its rows as HTML strings.
 *
 * Columns declare a `priority`, so a narrow screen drops the least important
 * first rather than scrolling everything sideways.
 *
 * @module components/ui/DataTable
 */

import React, { type ReactNode, useRef } from "react";

import { DataTableRow, PRIORITY_CELL, PRIORITY_CONTENT } from "./DataTableRow";
import { DataTableSkeleton } from "./DataTableSkeleton";
import { Icon } from "./Icon";
import { sortRows } from "./sort-rows";
import type { AsyncState } from "./useAsync";
import {
  type RowVirtualProps,
  useFrozenColumnWidths,
  type VirtualItem,
  VIRTUALIZE_AFTER,
  VirtualTableBody,
} from "./VirtualTableBody";

export interface Column<Row> {
  key: string;
  /** Sentence case, one or two words. */
  header: string;
  render: (row: Row) => ReactNode;
  /** Makes the column sortable. Return null to sort a row last. */
  sortValue?: (row: Row) => string | number | null;
  /**
   * Offers the sort control, defaulting to whether the column can sort itself.
   * Set it under `sortMode="external"`, where `sortValue` is never called and
   * declaring a dead one to unlock the control reads like live logic.
   */
  sortable?: boolean;
  /**
   * Hides the header text while keeping it as the column's accessible name,
   * for a column of controls whose heading would be wider than its contents.
   */
  hideHeader?: boolean;
  /** 1 always shows, 2 hides below `md`, 3 hides below `lg`. */
  priority?: 1 | 2 | 3;
  /**
   * Heads each row with this column's cell, as `<th scope="row">`. A grid of
   * marks needs it: without a row header, a screen reader reads a tick with
   * nothing naming the row it sits in.
   */
  rowHeader?: boolean;
  /** A Tailwind width class, for a column that must not collapse. */
  width?: string;
  align?: "start" | "end";
  /**
   * Where the column lands when the row is drawn as a card. Absent is a
   * labelled row in the card's body; the first column is the title unless
   * another claims it. `block` puts the label above the value, for prose a
   * side-by-side line would squeeze into half the card.
   */
  card?: "title" | "eyebrow" | "footer" | "hidden" | "block";
  /**
   * What the card shows in place of `render`, for a title that must be plain
   * text because the whole card opens it, or a cell too wide for a card.
   */
  cardRender?: (row: Row) => ReactNode;
}

export interface SortState {
  key: string;
  direction: "asc" | "desc";
}

/**
 * A row that opens to show more, loaded when it opens rather than up front.
 *
 * The detail carries its own `AsyncState`, so one row can be loading or failed
 * while the rest of the table is settled.
 */
export interface RowExpansion<Row> {
  /** Absent means every row expands. A row that cannot still keeps its cell. */
  canExpand?: (row: Row) => boolean;
  expandedKeys: ReadonlySet<string>;
  onToggle: (key: string) => void;
  /** The toggle's accessible name, e.g. `Curriculum history for X`. */
  label: (row: Row) => string;
  /**
   * Names the toggle, for a screen whose id a spec already clicks. Without it
   * the toggle takes `toggle-<the row's own id>`, which cannot spell an id
   * that puts the row's last.
   */
  toggleTestId?: (row: Row) => string;
  state: (row: Row) => AsyncState<unknown>;
  render: (row: Row, state: AsyncState<unknown>) => ReactNode;
}

/** A run of rows under one heading. */
export interface RowGroup<Row> {
  key: string;
  header: ReactNode;
  rows: readonly Row[];
}

export interface DataTableBase<Row> {
  columns: readonly Column<Row>[];
  rowKey: (row: Row) => string;
  /** Names the table for a screen reader. Visually hidden unless `showCaption`. */
  caption: string;
  showCaption?: boolean;
  /** Shown in place of the body when there are no rows. Required. */
  emptyState: ReactNode;
  /**
   * The rows have not arrived: the body is skeleton rows in the table's own
   * columns, so the layout does not move when they land.
   */
  loading?: boolean;
  /** Names what is loading, for the screen reader; empty when the caller announces it. Defaults to "Loading rows". */
  loadingLabel?: string;
  /** How many skeleton rows to draw. */
  loadingRows?: number;
  sort?: SortState;
  onSortChange?: (next: SortState) => void;
  /**
   * Who does the sorting. `external` draws the indicator and reports the
   * press without touching the row order, for a table the server already
   * ordered — reordering a page of it would contradict what was sent.
   */
  sortMode?: "client" | "external";
  density?: "sm" | "md";
  /**
   * Where a cell sits in a tall row. `top` for a table whose cells hold a
   * paragraph: centring the short ones against the long one leaves the row
   * with no line to read across.
   */
  verticalAlign?: "top" | "middle";
  stickyHeader?: boolean;
  /** Holds the first column in place while the rest scrolls, for a wide matrix. */
  stickyFirstColumn?: boolean;
  rowTestId?: (row: Row) => string;
  /** Extra classes for a row, such as a tint marking the rows a reader should look at. */
  rowClassName?: (row: Row) => string;
  expansion?: RowExpansion<Row>;
  className?: string;
  "data-testid"?: string;
}

/*
 * Flat or grouped, never both: a table given each would have to decide which
 * wins, and every caller would have to know which it chose.
 */
type RowSource<Row> =
  { rows: readonly Row[]; groups?: never } | { groups: readonly RowGroup<Row>[]; rows?: never };

export type DataTableProps<Row> = DataTableBase<Row> & RowSource<Row>;

/**
 * Which layer a header cell sits on.
 *
 * Two `z-` utilities on one element race, and which wins is decided by the
 * order Tailwind emits them rather than by anything here. The corner cell is
 * pinned on both axes and has to clear both neighbours.
 */
/*
 * The bottom rule a stuck header draws for itself.
 *
 * `border-collapse` gives the border to the table, so a header scrolled away
 * from its own row leaves the rule behind and the rows slide under a bare
 * edge. It replaces that border rather than joining it, or the rule is 2px at
 * rest and 1px once it moves. Measured on both axes: a pinned *column* keeps
 * the row's rule, which runs the full width underneath it, so an edge there
 * is only a second line and a step at the boundary.
 */
const STUCK_EDGE = "shadow-[inset_0_-1px_0_var(--color-base-300)]";

function headerLayer(stickyTop: boolean, stickyLeft: boolean): string {
  if (stickyTop && stickyLeft) {
    return "z-[3]";
  }
  if (stickyTop) {
    return "z-[2]";
  }
  return stickyLeft ? "z-[1]" : "";
}

/** How a sorted column announces itself, and which caret it shows. */
function sortAnnotation(
  sorted: boolean,
  direction: SortState["direction"] | undefined,
): { ariaSort: "ascending" | "descending" | undefined; icon: string } {
  if (!sorted) {
    return { ariaSort: undefined, icon: "caret-up-down" };
  }
  return direction === "asc"
    ? { ariaSort: "ascending", icon: "caret-up" }
    : { ariaSort: "descending", icon: "caret-down" };
}

/*
 * Dropping a column collapses its cell rather than removing it. `display: none`
 * takes a cell out of the table's column count, so a detail row spanning
 * `columns.length` would span more columns than are rendered and the table
 * invents the difference, widening past its container. The cell stays at zero
 * width and its contents are hidden through a `display: contents` wrapper,
 * which is layout-transparent while shown.
 */

/**
 * A name for a collapsed column, carried by a text node of its own.
 *
 * The cell has to stay in the row for the column count to hold, and a `<th>`
 * whose only text is `display: none` is an empty header, which axe reports at
 * every width the column is out. This node is `sr-only` while the column is
 * collapsed and gone above the breakpoint, where the real header is visible.
 *
 * A text node rather than `sr-only` on the header's own content, because that
 * content is a sort button when the column sorts, and `sr-only` would leave it
 * focusable and invisible — the same reason the body cells stay `hidden`.
 */
const PRIORITY_NAME: Record<NonNullable<Column<unknown>["priority"]>, string> = {
  1: "",
  2: "sr-only md:hidden",
  3: "sr-only lg:hidden",
};

/**
 * Whether a column may sort: the one rule behind the control, the caret and
 * the row order, which had drifted apart once already.
 */
function canSortColumn<Row>(
  column: Column<Row>,
  sortMode: "client" | "external",
  onSortChange: ((sort: SortState) => void) | undefined,
): boolean {
  return Boolean(
    onSortChange &&
    (column.sortable ?? Boolean(column.sortValue)) &&
    // Client-side sorting needs something to sort by; external does not.
    (sortMode === "external" || column.sortValue),
  );
}

/** The inputs that decide a collection's order, as `DataTable` takes them. */
export interface RowOrder<Row> {
  columns: readonly Column<Row>[];
  sort?: SortState | undefined;
  sortMode?: "client" | "external" | undefined;
  onSortChange?: ((sort: SortState) => void) | undefined;
}

/**
 * The order the table draws in, exported so a cards rendering of the same
 * collection draws the same one rather than source order.
 */
export function orderRows<Row>(rows: readonly Row[], order: RowOrder<Row>): readonly Row[] {
  const { columns, sort, sortMode = "client", onSortChange } = order;
  if (!sort || sortMode === "external") {
    return rows;
  }
  return sortRows(
    rows,
    columns.find(
      (column) => column.key === sort.key && canSortColumn(column, sortMode, onSortChange),
    ),
    sort.direction,
  );
}

/**
 * @example
 * ```tsx
 * <DataTable caption="Programmes" columns={columns} rows={rows} rowKey={(p) => p.id}
 *   emptyState={<EmptyState title="No programmes match these filters" density="row" />} />
 * ```
 */
export function DataTable<Row>({
  columns,
  rows = undefined,
  groups = undefined,
  rowKey,
  caption,
  showCaption = false,
  emptyState,
  loading = false,
  loadingLabel = "Loading rows",
  loadingRows = 4,
  sort = undefined,
  onSortChange = undefined,
  sortMode = "client",
  density = "sm",
  verticalAlign = "middle",
  stickyHeader = false,
  stickyFirstColumn = false,
  rowTestId = undefined,
  rowClassName = undefined,
  expansion = undefined,
  className = "",
  "data-testid": testId,
}: DataTableProps<Row>): React.ReactElement {
  const cell = density === "sm" ? "px-4 py-2" : "px-4 py-3";
  const rowAlign = verticalAlign === "top" ? "align-top" : "align-middle";
  // One shape for both sources, so the body is written once.
  const sections = groups ?? [{ key: "", header: null, rows: rows ?? [] }];
  const total = sections.reduce((count, group) => count + group.rows.length, 0);
  // The toggle's column exists to hold row toggles, so an empty table has none
  // and its header starts where its empty state does.
  const hasToggle = expansion !== undefined && total > 0;
  const span = columns.length + (hasToggle ? 1 : 0);
  const canSort = (column: Column<Row>): boolean => canSortColumn(column, sortMode, onSortChange);

  // Only a table with its own scroller can window its rows, and an expanded
  // row's detail is a second row the window would have to track.
  const virtual = stickyHeader && expansion === undefined && total > VIRTUALIZE_AFTER;
  const virtualItems: VirtualItem<Row>[] = virtual
    ? sections.flatMap((group) => [
        ...(group.header === null
          ? []
          : [{ kind: "group" as const, key: `group:${group.key}`, header: group.header }]),
        ...orderRows(group.rows, { columns, sort, sortMode, onSortChange }).map((row) => ({
          kind: "row" as const,
          key: rowKey(row),
          row,
        })),
      ])
    : [];
  const scrollRef = useRef<HTMLDivElement>(null);
  const { widths, tableRef } = useFrozenColumnWidths(scrollRef, virtual);

  const toggle = (column: Column<Row>): void => {
    // `onSortChange` again, for the narrowing: a check inside `canSort` is
    // one the compiler cannot see through.
    if (!onSortChange || !canSort(column)) {
      return;
    }
    onSortChange({
      key: column.key,
      direction: sort?.key === column.key && sort.direction === "asc" ? "desc" : "asc",
    });
  };

  const drawRow = (row: Row, virtualProps?: RowVirtualProps): React.ReactElement => (
    <DataTableRow
      key={rowKey(row)}
      row={row}
      columns={columns}
      rowKey={rowKey}
      rowTestId={rowTestId}
      rowClassName={rowClassName}
      expansion={expansion}
      stickyFirstColumn={stickyFirstColumn}
      rowAlign={rowAlign}
      cell={cell}
      span={span}
      virtualProps={virtualProps}
    />
  );

  const drawGroupHeader = (
    header: ReactNode,
    virtualProps?: RowVirtualProps,
  ): React.ReactElement => (
    <tr {...virtualProps}>
      <th
        scope="rowgroup"
        colSpan={span}
        className={`border-b border-base-300 bg-base-200 text-left ${cell}`}
      >
        {header}
      </th>
    </tr>
  );

  return (
    <div
      className={[
        /* relative: `sr-only` is absolutely positioned, so a hidden heading
           with no positioned ancestor escapes this scroller and pushes the
           document sideways — 24px of it on the programmes list. Here rather
           than on the cell, which takes `sticky` and would compete. */
        "relative overflow-x-auto rounded-box border border-base-300 bg-base-100",
        // The shadows' covers are `background-attachment: local`, which stops
        // the browser compositing a scroll: on a table that scrolls down as
        // well, every frame repaints every row.
        stickyHeader ? "" : "sq-scroll-shadow",
        // A height, or `sticky top-0` has nothing to stick within: this
        // wrapper is already the nearest scrolling ancestor (overflow-x-auto
        // computes overflow-y to auto), and unbounded it always grows to the
        // table's own height and never scrolls, so the header scrolls away
        // with the page exactly as if the prop were unset.
        stickyHeader ? "max-h-[70vh] overflow-y-auto" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      ref={scrollRef}
      data-testid={testId}
    >
      <table
        ref={tableRef}
        className={`w-full border-collapse text-left text-sm ${widths ? "table-fixed" : ""}`.trim()}
        aria-busy={loading || undefined}
        // The rows a windowed body leaves out still count, so a screen reader
        // can say where in the table a drawn row sits.
        aria-rowcount={virtual ? virtualItems.length + 1 : undefined}
      >
        <caption className={showCaption ? "px-4 py-3 text-left text-sm text-muted" : "sr-only"}>
          {caption}
        </caption>
        {widths && (
          <colgroup>
            {columns.map((column, index) => (
              <col key={column.key} style={{ width: widths[index] }} />
            ))}
          </colgroup>
        )}
        <thead>
          <tr>
            {columns.map((column, index) => {
              const sortable = canSort(column);
              const sorted = sortable && sort?.key === column.key;
              const { ariaSort, icon } = sortAnnotation(sorted, sort?.direction);
              return (
                <th
                  key={column.key}
                  scope="col"
                  // Carbon: header and body rows share a height, headers are
                  // sentence case, and hover is always on for scanning.
                  className={[
                    "bg-base-200 text-xs font-semibold tracking-[0.06em] text-muted uppercase",
                    stickyHeader ? STUCK_EDGE : "border-b border-base-300",
                    cell,
                    column.align === "end" ? "text-right" : "text-left",
                    column.width ?? "",
                    PRIORITY_CELL[column.priority ?? 1],
                    stickyHeader ? "sticky top-0" : "",
                    stickyFirstColumn && index === 0 ? "sticky left-0" : "",
                    headerLayer(stickyHeader, stickyFirstColumn && index === 0),
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-sort={ariaSort}
                >
                  {column.priority && column.priority > 1 ? (
                    <span data-priority-name="" className={PRIORITY_NAME[column.priority]}>
                      {column.header}
                    </span>
                  ) : null}
                  <span data-priority-content="" className={PRIORITY_CONTENT[column.priority ?? 1]}>
                    {sortable ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 font-semibold uppercase"
                        onClick={() => {
                          toggle(column);
                        }}
                      >
                        <span className={column.hideHeader ? "sr-only" : undefined}>
                          {column.header}
                        </span>
                        <Icon name={icon} aria-hidden />
                      </button>
                    ) : (
                      <span className={column.hideHeader ? "sr-only" : undefined}>
                        {column.header}
                      </span>
                    )}
                  </span>
                </th>
              );
            })}
            {hasToggle && (
              <th
                scope="col"
                className={[
                  "w-12 bg-base-200",
                  stickyHeader ? STUCK_EDGE : "border-b border-base-300",
                  cell,
                  stickyHeader ? "sticky top-0" : "",
                  headerLayer(stickyHeader, false),
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <span className="sr-only">Expand</span>
              </th>
            )}
          </tr>
        </thead>
        {loading ? (
          <DataTableSkeleton rows={loadingRows} columns={span} cell={cell} label={loadingLabel} />
        ) : null}
        {!loading && total === 0 ? (
          <tbody>
            <tr>
              <td colSpan={span} className="p-4">
                {emptyState}
              </td>
            </tr>
          </tbody>
        ) : null}
        {!loading && total > 0 && virtual ? (
          <VirtualTableBody
            items={virtualItems}
            scrollRef={scrollRef}
            estimateSize={density === "sm" ? 41 : 49}
            span={span}
            renderItem={(item, props) =>
              item.kind === "group" ? drawGroupHeader(item.header, props) : drawRow(item.row, props)
            }
          />
        ) : null}
        {!loading &&
          total > 0 &&
          !virtual &&
          sections.map((group) => (
            /*
             * One `tbody` per group, so the heading has a row group to head.
             * `scope="rowgroup"` assigns a heading to its `tbody`; a colgroup
             * would head nothing, since the table renders none.
             */
            <tbody key={group.key}>
              {group.header !== null && drawGroupHeader(group.header)}
              {orderRows(group.rows, { columns, sort, sortMode, onSortChange }).map((row) =>
                drawRow(row),
              )}
            </tbody>
          ))}
      </table>
    </div>
  );
}
